import { useMemo } from "react";

import { formatAwsDateString, formatCurrency } from "@covertree/utils";
import { Row } from "@covertree/ui/table/types";

import {
  GetQuoteFilterByStatus,
  QuoteListItemFragment,
} from "@covertree/agent-portal-graphql";

import { formatProductTypeName } from "@utils/formatPolicyType";
import { useContinueQuote } from "@providers/ContinueQuoteProvider";
import { useUWReason } from "@providers/UWReasonProvider";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { featureFlags } from "@utils/featureFlags";

export const useMappedQuotes = (
  quotes: QuoteListItemFragment[] | undefined,
  statusFilter: GetQuoteFilterByStatus
): Row[] => {
  const { canWriteBusiness: canContinueQuote } = useAccessPattern();
  const { showContinueQuoteModal } = useContinueQuote();
  const { showUWReasonModal } = useUWReason();

  return useMemo(() => {
    if (!quotes) return [];

    return quotes.map<Row>((quote) => ({
      id: quote.selectedQuoteLocator,
      data: [
        quote.policyLocator,
        quote.selectedQuoteLocator,
        `${quote.policyholder.firstName} ${quote.policyholder.lastName}`,
        [
          quote.previousAddress.streetAddress,
          quote.previousAddress.city,
          quote.previousAddress.state,
          quote.previousAddress.zipCode,
        ].join(" "),
        formatProductTypeName(quote.productTypeName),
        formatCurrency(quote.units[0].pricing.grossPremium),
        quote.agencyInformation.agentName,
        quote.agencyInformation.agencyName,
        formatAwsDateString(quote.createdDate),
        formatAwsDateString(quote.updatedDate),
      ],
      ...(() => {
        const actions = [];

        if (
          canContinueQuote &&
          [
            GetQuoteFilterByStatus.Valid,
            GetQuoteFilterByStatus.Pending,
          ].includes(statusFilter)
        ) {
          actions.push({
            label: "Continue quote",
            onClick: () =>
              showContinueQuoteModal(
                quote.policyLocator,
                quote.startDateAfterUWReview
              ),
          });
        }

        if (
          featureFlags.viewUWReasons.isEnabled() &&
          statusFilter === GetQuoteFilterByStatus.Pending
        ) {
          actions.push({
            label: "View reason",
            onClick: () => showUWReasonModal(quote.policyLocator),
          });
        }

        return actions.length ? { actionMenuItems: actions } : {};
      })(),
    }));
  }, [quotes, statusFilter]);
};
